import React, { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { CustomItem, Header, Section, SkeletonItem } from '@atlaskit/side-navigation';
import { type AnalyticsAttributes, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { isServer } from '@atlassian/eoc-common';
import { Flex } from '@atlassian/eoc-flex';

import { SPOTLIGHT_NAMES } from '../../common/constants';
import { useReportsController } from '../../controllers/sidebar';

import { LinkItem } from './link-item';
import messages from './messages';
import { Container } from './styled';

export const Sidebar = ({ baseRoute }: { baseRoute: string }) => {
	const { navigationItems, isLoading, teamId, dashboardId, isGlobal, error, hideItemsInSidebar } =
		useReportsController();
	const { formatMessage } = useIntl();
	const selectedItemRef = useRef<HTMLElement>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = useCallback(
		(attributes: AnalyticsAttributes) => () => {
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'navigationItem clicked',
				'opsReportsMenuItem',
				attributes,
			);
		},
		[createAnalyticsEvent],
	);

	useEffect(() => {
		const target = selectedItemRef.current;

		if (target) {
			// Target is outside the viewport from the bottom
			if (target.getBoundingClientRect().bottom > window.innerHeight) {
				//  The bottom of the target will be aligned to the bottom of the visible area of the scrollable ancestor.
				target.scrollIntoView(false);
			}

			// Target is outside the view from the top
			if (target.getBoundingClientRect().top < 0) {
				// The top of the target will be aligned to the top of the visible area of the scrollable ancestor
				target.scrollIntoView();
			}
		}
	}, [dashboardId]);

	let component: React.ReactNode;

	if (isServer() || isLoading) {
		component = (
			<>
				<SkeletonItem />
				<SkeletonItem />
				<SkeletonItem />
				<SkeletonItem />
				<SkeletonItem />
			</>
		);
	} else if (error || hideItemsInSidebar) {
		component = null;
	} else {
		component = navigationItems.map((section, index) => {
			return (
				<Section title={section.name} key={index}>
					{section.dashboards.map((menuItem) => {
						const isSelected = dashboardId === menuItem.dashboardId;
						return (
							<CustomItem
								ref={isSelected ? selectedItemRef : null}
								isSelected={isSelected}
								onClick={onClick({
									selectedTeamId: teamId,
									selectedDashboardId: menuItem.dashboardId,
									isGlobal,
								})}
								key={`${index}-${menuItem.dashboardId}`}
								href={`${baseRoute}/${menuItem.dashboardId}?selectedTeamId=${teamId}&global=${isGlobal}`}
								component={LinkItem}
							>
								{menuItem.name}
							</CustomItem>
						);
					})}
				</Section>
			);
		});
	}

	return !!component ? (
		<Container>
			{navigationItems.length > 0 ? (
				<SpotlightTarget name={SPOTLIGHT_NAMES.sidebarReportItems}>
					<div>
						<Header>{formatMessage(messages.header)}</Header>
						<Flex flexDirection="column">{component}</Flex>
					</div>
				</SpotlightTarget>
			) : (
				<Flex flexDirection="column">{component}</Flex>
			)}
		</Container>
	) : null;
};
